import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import {
  Crown, Heart, Gift, Link2, Sparkles, Users, Building2, Search,
  Plus, X, Save, Loader2, Copy, Check, QrCode, Send, ExternalLink, Wand2,
} from "lucide-react";
import AIContentCreatorUpsell from "@/components/AIContentCreatorUpsell";
import { toast } from "sonner";
import { buildListingShareUrl } from "@/utils/shareUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

// --- Helpers ---
const isAgentListing = (l) => {
  if (!l) return false;
  if (l.is_individual === true) return true;
  const cat = (l.category_name || "").toLowerCase();
  return /agent|realtor|broker|loan officer|consultant|advisor|insurance/.test(cat);
};

const participationMode = (l) => {
  if (!l) return "business";
  if (l.listing_kind === "creator") return "creator";
  if (isAgentListing(l)) return "agent";
  return "business";
};

const MODE_COPY = {
  business: {
    label: "Business Promoter Mode",
    description:
      "You're participating in OnlyBest as a business. Recommend other businesses, partners, causes, and offers right from your existing listing.",
    accent: "from-[#7C3AED] to-[#3B82F6]",
    icon: Building2,
  },
  agent: {
    label: "Agent / Professional Promoter Mode",
    description:
      "As a loan officer, realtor, or professional, you can recommend trusted vendors, community resources, and causes — keeping your existing agent profile.",
    accent: "from-[#10B981] to-[#3B82F6]",
    icon: Users,
  },
  creator: {
    label: "Individual Creator",
    description:
      "Your OnlyBest creator profile lets you share your personal Best of Best picks, causes, partner offers, and shareable links.",
    accent: "from-[#FBBF24] to-[#EC4899]",
    icon: Crown,
  },
};

// --- Small reusable subcomponents ---
function EditableList({ items, fields, onChange, addLabel, testid }) {
  const update = (idx, key, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: value };
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, fields.reduce((a, f) => ({ ...a, [f.key]: "" }), {})]);

  return (
    <div className="space-y-3" data-testid={testid}>
      {items.map((row, idx) => (
        <div key={idx} className="rounded-xl border border-white/10 bg-white/5 p-3 sm:p-4 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <p className="text-xs uppercase tracking-wider text-[#64748B]">Item #{idx + 1}</p>
            <button
              onClick={() => remove(idx)}
              className="text-[#94A3B8] hover:text-[#EF4444] transition-colors"
              data-testid={`${testid}-remove-${idx}`}
              aria-label="Remove item"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "sm:col-span-2 min-w-0" : "min-w-0"}>
                <label className="block text-xs text-[#94A3B8] mb-1">{f.label}</label>
                {f.textarea ? (
                  <textarea
                    value={row[f.key] || ""}
                    onChange={(e) => update(idx, f.key, e.target.value)}
                    rows={2}
                    placeholder={f.placeholder}
                    className="w-full px-3 py-2 bg-[#0F172A] border border-white/10 rounded-lg text-white text-sm placeholder:text-[#475569] focus:outline-none focus:border-[#7C3AED]/60"
                  />
                ) : (
                  <input
                    value={row[f.key] || ""}
                    onChange={(e) => update(idx, f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className="w-full px-3 py-2 bg-[#0F172A] border border-white/10 rounded-lg text-white text-sm placeholder:text-[#475569] focus:outline-none focus:border-[#7C3AED]/60"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <button
        onClick={add}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-white/20 text-[#94A3B8] hover:text-white hover:border-white/40 transition"
        data-testid={`${testid}-add`}
      >
        <Plus className="w-4 h-4" /> {addLabel}
      </button>
    </div>
  );
}

// Search the platform for listings to pin as picks
function PicksManager({ picks, onChange, getAuthHeader }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const runSearch = async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await axios.get(`${API}/onlybest/search-listings?q=${encodeURIComponent(q)}`, {
        headers: getAuthHeader(),
      });
      setResults(r.data.results || []);
    } catch (_) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const addPick = (b) => {
    if (picks.find((p) => p.slug === b.slug)) {
      toast.info("Already in your picks");
      return;
    }
    onChange([...picks, { slug: b.slug, name: b.name, city: b.city, state: b.state, logo_url: b.logo_url || "", note: "" }]);
    toast.success(`Added ${b.name}`);
  };

  const removePick = (slug) => onChange(picks.filter((p) => p.slug !== slug));
  const updateNote = (slug, note) => onChange(picks.map((p) => (p.slug === slug ? { ...p, note } : p)));

  return (
    <div className="space-y-4" data-testid="picks-manager">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); runSearch(e.target.value); }}
          placeholder="Search LocalBest listings to add as picks..."
          className="w-full pl-10 pr-4 py-2.5 bg-[#0F172A] border border-white/10 rounded-xl text-white text-sm placeholder:text-[#475569] focus:outline-none focus:border-[#7C3AED]/60"
          data-testid="picks-search-input"
        />
      </div>
      {searching && <p className="text-xs text-[#64748B]">Searching…</p>}
      {results.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-[#0F172A] divide-y divide-white/5 max-h-64 overflow-y-auto">
          {results.map((b) => (
            <button
              key={b.slug}
              onClick={() => addPick(b)}
              className="w-full text-left flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 transition min-w-0"
              data-testid={`picks-result-${b.slug}`}
            >
              {b.logo_url ? (
                <img src={b.logo_url} alt="" className="w-9 h-9 rounded object-cover bg-white/10 flex-shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-[#94A3B8]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{b.name}</p>
                <p className="text-[#64748B] text-xs truncate">
                  {b.category_name || "—"}{b.city ? ` · ${b.city}, ${b.state || ""}` : ""}
                </p>
              </div>
              <Plus className="w-4 h-4 text-[#94A3B8] flex-shrink-0" />
            </button>
          ))}
        </div>
      )}

      {picks.length === 0 ? (
        <p className="text-sm text-[#64748B] text-center py-6">No picks yet. Search above and add the businesses you recommend.</p>
      ) : (
        <div className="space-y-2">
          {picks.map((p) => (
            <div key={p.slug} className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-white/5 min-w-0">
              {p.logo_url ? (
                <img src={p.logo_url} alt="" className="w-10 h-10 rounded object-cover bg-white/10 flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-[#94A3B8]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{p.name}</p>
                <input
                  value={p.note || ""}
                  onChange={(e) => updateNote(p.slug, e.target.value)}
                  placeholder="Why do you recommend them? (optional)"
                  className="mt-1 w-full px-2 py-1 bg-[#0F172A] border border-white/10 rounded text-white text-xs placeholder:text-[#475569] focus:outline-none focus:border-[#7C3AED]/60"
                />
              </div>
              <button
                onClick={() => removePick(p.slug)}
                className="text-[#94A3B8] hover:text-[#EF4444] flex-shrink-0"
                aria-label="Remove pick"
                data-testid={`picks-remove-${p.slug}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Main page ---
export default function OnlyBestCampaign() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listing, setListing] = useState(null);

  // Editable fields (local state)
  const [enabled, setEnabled] = useState(false);
  const [tagline, setTagline] = useState("");
  const [picks, setPicks] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [causes, setCauses] = useState([]);
  const [offers, setOffers] = useState([]);
  const [affiliateLinks, setAffiliateLinks] = useState([]);

  // Invite UI
  const [inviteForm, setInviteForm] = useState({ invite_type: "creator", invitee_email: "", invitee_name: "", note: "" });
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteSending, setInviteSending] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const mode = useMemo(() => participationMode(listing), [listing]);
  const ModeIcon = MODE_COPY[mode].icon;

  const publicProfileUrl = useMemo(() => {
    if (!listing?.slug) return "";
    return buildListingShareUrl(listing);
  }, [listing]);

  const shareUrl = useMemo(() => {
    if (!listing?.slug) return "";
    const base = buildListingShareUrl(listing);
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}utm_source=onlybest&utm_medium=share&utm_campaign=onlybest_${listing.slug}`;
  }, [listing]);

  const qrUrl = useMemo(() => {
    if (!shareUrl) return "";
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&format=png&margin=10&data=${encodeURIComponent(shareUrl)}`;
  }, [shareUrl]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/listings/my`, { headers: getAuthHeader() })
      .then((r) => {
        if (cancelled) return;
        const l = r.data;
        setListing(l);
        if (l) {
          setEnabled(Boolean(l.onlybest_enabled));
          setTagline(l.onlybest_tagline || "");
          setPicks(Array.isArray(l.onlybest_picks) ? l.onlybest_picks : []);
          setRecommendations(Array.isArray(l.onlybest_recommendations) ? l.onlybest_recommendations : []);
          setCauses(Array.isArray(l.onlybest_causes) ? l.onlybest_causes : []);
          setOffers(Array.isArray(l.onlybest_offers) ? l.onlybest_offers : []);
          setAffiliateLinks(Array.isArray(l.onlybest_affiliate_links) ? l.onlybest_affiliate_links : []);
        }
      })
      .catch(() => { if (!cancelled) setListing(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [getAuthHeader]);

  const save = async () => {
    if (!listing?.slug) {
      toast.error("You need an approved listing first.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        onlybest_enabled: enabled,
        onlybest_tagline: tagline,
        onlybest_picks: picks,
        onlybest_recommendations: recommendations,
        onlybest_causes: causes,
        onlybest_offers: offers,
        onlybest_affiliate_links: affiliateLinks,
      };
      const r = await axios.patch(`${API}/listings/${listing.slug}`, payload, { headers: getAuthHeader() });
      setListing(r.data);
      toast.success("OnlyBest campaign saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success("Copied");
      setTimeout(() => setCopiedField(null), 1500);
    } catch (_) {
      toast.error("Copy failed");
    }
  };

  const sendInvite = async () => {
    if (!listing?.slug) { toast.error("Approved listing required."); return; }
    setInviteSending(true);
    try {
      const r = await axios.post(`${API}/onlybest/invite`, inviteForm, { headers: getAuthHeader() });
      setInviteResult(r.data);
      toast.success("Invite link ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally {
      setInviteSending(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="onlybest-loading">
        <div className="h-28 bg-white/5 rounded-2xl animate-pulse" />
        <div className="h-64 bg-white/5 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center" data-testid="onlybest-no-listing">
        <Crown className="w-10 h-10 text-[#FBBF24] mx-auto mb-3" />
        <p className="text-white font-semibold">Create or claim your listing first.</p>
        <p className="text-[#94A3B8] text-sm mt-1">Once approved, you can turn on your OnlyBest Campaign here.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="onlybest-campaign-page">
      {/* Hero / mode banner */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className={`relative overflow-hidden rounded-2xl border border-white/10 p-5 sm:p-6 bg-gradient-to-br ${MODE_COPY[mode].accent}`}
      >
        <div className="absolute inset-0 bg-[#0A0F1C]/70" />
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center flex-shrink-0">
            <ModeIcon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-white/70">OnlyBest Campaign</p>
            <h1 className="text-xl sm:text-2xl font-bold text-white font-['Outfit'] mt-0.5">{MODE_COPY[mode].label}</h1>
            <p className="text-sm text-white/80 mt-1">{MODE_COPY[mode].description}</p>
          </div>
          <label className="inline-flex items-center gap-3 cursor-pointer flex-shrink-0" data-testid="onlybest-enabled-toggle">
            <span className="text-sm text-white/80">{enabled ? "Active" : "Inactive"}</span>
            <span className="relative inline-block w-12 h-7">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="peer opacity-0 absolute inset-0 cursor-pointer"
                data-testid="onlybest-enabled-checkbox"
              />
              <span className="block w-12 h-7 rounded-full bg-white/20 peer-checked:bg-[#FBBF24] transition-colors" />
              <span className="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
            </span>
          </label>
        </div>
      </motion.div>

      {/* Dashboard-only upsell — AI Content Creator */}
      <AIContentCreatorUpsell />

      {/* Tagline */}
      <section className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#FBBF24]" /> Campaign tagline
        </h2>
        <input
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          placeholder='e.g. "Promoting the best of our community — small businesses, big hearts."'
          className="w-full px-3 py-2.5 bg-[#0F172A] border border-white/10 rounded-xl text-white text-sm placeholder:text-[#475569] focus:outline-none focus:border-[#FBBF24]/60"
          data-testid="onlybest-tagline-input"
        />
        <p className="text-xs text-[#64748B] mt-2">Shows above your OnlyBest sections on your public page.</p>
      </section>

      {/* My Best of Best Picks */}
      <section className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Crown className="w-4 h-4 text-[#FBBF24]" />
            <h2 className="text-base sm:text-lg font-semibold text-white">My Best of Best Picks</h2>
          </div>
          <AiSuggestButton
            listingSlug={listing.slug}
            getAuthHeader={getAuthHeader}
            onApply={(items) => {
              const merged = [...picks];
              for (const it of items) {
                if (!merged.find((p) => p.slug === it.slug)) merged.push({ slug: it.slug, name: it.slug, note: it.note || "" });
              }
              setPicks(merged);
              toast.success("AI suggestions added — review & save");
            }}
          />
        </div>
        <p className="text-xs text-[#94A3B8] mb-4">Pin LocalBest.ai listings you personally recommend. They show up on your public page as your curated picks.</p>
        <PicksManager picks={picks} onChange={setPicks} getAuthHeader={getAuthHeader} />
      </section>

      {/* Recommendations (external) */}
      <section className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <Building2 className="w-4 h-4 text-[#3B82F6]" />
          <h2 className="text-base sm:text-lg font-semibold text-white">{mode === "agent" ? "Trusted Vendors & Partners I Recommend" : "Businesses I Recommend"}</h2>
        </div>
        <p className="text-xs text-[#94A3B8] mb-4">Recommend businesses that aren't on LocalBest.ai yet.</p>
        <EditableList
          items={recommendations}
          onChange={setRecommendations}
          addLabel="Add a recommendation"
          testid="recommendations"
          fields={[
            { key: "name", label: "Name", placeholder: "Business name" },
            { key: "url", label: "Website", placeholder: "https://…" },
            { key: "description", label: "Why you recommend them", textarea: true, full: true, placeholder: "Optional note shown publicly" },
          ]}
        />
      </section>

      {/* Causes */}
      <section className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <Heart className="w-4 h-4 text-[#EC4899]" />
          <h2 className="text-base sm:text-lg font-semibold text-white">Causes I Support</h2>
        </div>
        <EditableList
          items={causes}
          onChange={setCauses}
          addLabel="Add a cause"
          testid="causes"
          fields={[
            { key: "name", label: "Cause / Non-profit name", placeholder: "e.g. Habitat for Humanity" },
            { key: "url", label: "Donate / Learn-more URL", placeholder: "https://…" },
            { key: "description", label: "Why this matters to you", textarea: true, full: true },
          ]}
        />
      </section>

      {/* Offers / Partner Deals */}
      <section className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <Gift className="w-4 h-4 text-[#10B981]" />
          <h2 className="text-base sm:text-lg font-semibold text-white">Partner Offers I Share</h2>
        </div>
        <EditableList
          items={offers}
          onChange={setOffers}
          addLabel="Add an offer"
          testid="offers"
          fields={[
            { key: "title", label: "Offer title", placeholder: "e.g. 10% off first roof inspection" },
            { key: "code", label: "Promo code (optional)", placeholder: "BESTROOF10" },
            { key: "url", label: "Redeem URL", placeholder: "https://…", full: true },
            { key: "description", label: "Details", textarea: true, full: true },
          ]}
        />
      </section>

      {/* Affiliate / referral links */}
      <section className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <Link2 className="w-4 h-4 text-[#A78BFA]" />
          <h2 className="text-base sm:text-lg font-semibold text-white">Affiliate / Referral Links</h2>
        </div>
        <EditableList
          items={affiliateLinks}
          onChange={setAffiliateLinks}
          addLabel="Add an affiliate link"
          testid="affiliate-links"
          fields={[
            { key: "label", label: "Label", placeholder: "e.g. My favorite CRM" },
            { key: "url", label: "Link", placeholder: "https://…" },
            { key: "description", label: "Description", textarea: true, full: true },
          ]}
        />
      </section>

      {/* Sticky save bar */}
      <div className="sticky bottom-4 z-10">
        <div className="rounded-2xl border border-[#FBBF24]/30 bg-[#0F172A]/95 backdrop-blur px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs sm:text-sm text-[#94A3B8]">Changes are only applied after you save.</p>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#FBBF24] to-[#EC4899] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
            data-testid="onlybest-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save campaign
          </button>
        </div>
      </div>

      {/* Share tools */}
      <section className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <QrCode className="w-4 h-4 text-[#FBBF24]" /> My OnlyBest Share Tools
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-start">
          <div className="space-y-3 min-w-0">
            <div>
              <label className="block text-xs text-[#94A3B8] mb-1">My public OnlyBest page</label>
              <div className="flex items-center gap-2 min-w-0">
                <input value={publicProfileUrl} readOnly className="flex-1 px-3 py-2 bg-[#0F172A] border border-white/10 rounded-lg text-white text-xs sm:text-sm font-mono truncate" data-testid="onlybest-public-url" />
                <button onClick={() => copyToClipboard(publicProfileUrl, "public")} className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white flex-shrink-0" data-testid="copy-public-url">
                  {copiedField === "public" ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs text-[#94A3B8] mb-1">Trackable share URL (with UTM)</label>
              <div className="flex items-center gap-2 min-w-0">
                <input value={shareUrl} readOnly className="flex-1 px-3 py-2 bg-[#0F172A] border border-white/10 rounded-lg text-white text-xs sm:text-sm font-mono truncate" data-testid="onlybest-tracked-url" />
                <button onClick={() => copyToClipboard(shareUrl, "tracked")} className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white flex-shrink-0" data-testid="copy-tracked-url">
                  {copiedField === "tracked" ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <a href={publicProfileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[#FBBF24] hover:text-white">
              <ExternalLink className="w-4 h-4" /> Preview my public page
            </a>
          </div>
          {qrUrl && (
            <div className="rounded-xl border border-white/10 bg-white p-2 flex-shrink-0 self-center md:self-start" data-testid="onlybest-qr">
              <img src={qrUrl} alt="OnlyBest share QR code" className="w-32 h-32" />
            </div>
          )}
        </div>
      </section>

      {/* Invites */}
      <section className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Send className="w-4 h-4 text-[#3B82F6]" /> Invite someone
        </h2>
        <p className="text-xs text-[#94A3B8] mb-4">
          Invite an individual to become an <strong>OnlyBest Creator</strong>, or invite a <strong>business</strong> to start its own affiliate program on LocalBest.ai. We'll generate a tracked link tied to your listing.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 flex gap-2">
            <button
              onClick={() => setInviteForm((f) => ({ ...f, invite_type: "creator" }))}
              className={`flex-1 px-3 py-2 rounded-lg text-sm border transition ${inviteForm.invite_type === "creator" ? "border-[#FBBF24] bg-[#FBBF24]/10 text-white" : "border-white/10 text-[#94A3B8] hover:text-white"}`}
              data-testid="invite-type-creator"
            >Invite a Creator</button>
            <button
              onClick={() => setInviteForm((f) => ({ ...f, invite_type: "business" }))}
              className={`flex-1 px-3 py-2 rounded-lg text-sm border transition ${inviteForm.invite_type === "business" ? "border-[#3B82F6] bg-[#3B82F6]/10 text-white" : "border-white/10 text-[#94A3B8] hover:text-white"}`}
              data-testid="invite-type-business"
            >Invite a Business</button>
          </div>
          <input
            value={inviteForm.invitee_name}
            onChange={(e) => setInviteForm((f) => ({ ...f, invitee_name: e.target.value }))}
            placeholder="Name (optional)"
            className="px-3 py-2 bg-[#0F172A] border border-white/10 rounded-lg text-white text-sm placeholder:text-[#475569] focus:outline-none focus:border-[#7C3AED]/60"
            data-testid="invite-name-input"
          />
          <input
            value={inviteForm.invitee_email}
            onChange={(e) => setInviteForm((f) => ({ ...f, invitee_email: e.target.value }))}
            placeholder="Email (optional, for your records)"
            className="px-3 py-2 bg-[#0F172A] border border-white/10 rounded-lg text-white text-sm placeholder:text-[#475569] focus:outline-none focus:border-[#7C3AED]/60"
            data-testid="invite-email-input"
          />
          <textarea
            value={inviteForm.note}
            onChange={(e) => setInviteForm((f) => ({ ...f, note: e.target.value }))}
            rows={2}
            placeholder="Personal note (optional)"
            className="sm:col-span-2 px-3 py-2 bg-[#0F172A] border border-white/10 rounded-lg text-white text-sm placeholder:text-[#475569] focus:outline-none focus:border-[#7C3AED]/60"
            data-testid="invite-note-input"
          />
        </div>
        <button
          onClick={sendInvite}
          disabled={inviteSending}
          className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#3B82F6] to-[#7C3AED] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
          data-testid="invite-send-btn"
        >
          {inviteSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Generate invite link
        </button>

        {inviteResult && (
          <div className="mt-4 rounded-xl border border-[#10B981]/30 bg-[#10B981]/5 p-4" data-testid="invite-result">
            <p className="text-sm text-white">Share this link with {inviteResult.invite.invitee_name || "your invitee"}:</p>
            <div className="mt-2 flex items-center gap-2 min-w-0">
              <input value={inviteResult.share_url} readOnly className="flex-1 px-3 py-2 bg-[#0F172A] border border-white/10 rounded-lg text-white text-xs sm:text-sm font-mono truncate" />
              <button onClick={() => copyToClipboard(inviteResult.share_url, "invite")} className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white flex-shrink-0" data-testid="copy-invite-url">
                {copiedField === "invite" ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-[#94A3B8] mt-2">When they sign up, your listing slug ({listing.slug}) is recorded as the referrer.</p>
          </div>
        )}
      </section>
    </div>
  );
}

// AI Suggest button — calls /onlybest/ai-suggest and surfaces the parsed list.
function AiSuggestButton({ listingSlug, getAuthHeader, onApply }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const run = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/onlybest/ai-suggest`, {
        listing_slug: listingSlug,
        suggestion_type: "picks",
      }, { headers: getAuthHeader() });
      const items = Array.isArray(r.data.suggestions) ? r.data.suggestions : [];
      setSuggestions(items);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI suggest failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={run}
        disabled={loading}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white text-xs sm:text-sm font-semibold hover:opacity-90 disabled:opacity-60"
        data-testid="ai-suggest-picks-btn"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
        AI Suggest Picks
      </button>
      {open && suggestions.length > 0 && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 rounded-2xl border border-white/10 bg-[#0F172A] shadow-2xl z-20 p-3" data-testid="ai-suggestions-list">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wider text-[#FBBF24]">AI Suggestions</p>
            <button onClick={() => setOpen(false)} className="text-[#94A3B8] hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <ul className="space-y-2 max-h-80 overflow-y-auto">
            {suggestions.map((s, i) => (
              <li key={i} className="rounded-lg border border-white/10 bg-white/5 p-2 min-w-0">
                <p className="text-white text-xs font-mono truncate">{s.slug}</p>
                {s.note && <p className="text-[11px] text-[#94A3B8] mt-0.5 line-clamp-2">{s.note}</p>}
              </li>
            ))}
          </ul>
          <button
            onClick={() => { onApply(suggestions); setOpen(false); }}
            className="mt-3 w-full px-3 py-2 rounded-full bg-gradient-to-r from-[#FBBF24] to-[#EC4899] text-white text-xs font-semibold"
            data-testid="ai-suggestions-apply"
          >
            Add all to my picks
          </button>
        </div>
      )}
    </div>
  );
}

